import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  ChevronDown,
  Plus,
  Edit,
  Trash2,
  MoreVertical,
  Mail,
} from "lucide-react";
import AdminFaqForm from "./AdminFaqForm";
import { Button } from "../ui/button";
import { DeleteConfirmationModal } from "../ui/delete-confirmation-modal";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { adminFaqService, FAQ } from "../../services/admin/faq.admin.service";
import { toast } from "react-hot-toast";

type Priority = number;

// Function to process content and convert YouTube links to iframes
function processYouTubeContent(content: string): string {
  if (!content) return content;

  // First, handle YouTube links that are wrapped in <a> tags by TipTap
  let processedContent = content.replace(
    /<a[^>]*href="(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+))"[^>]*>.*?<\/a>/g,
    (_, _url, videoId) => {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
    }
  );

  // Then handle any plain YouTube URLs that might not be in links
  processedContent = processedContent.replace(
    /(?<!href=["'])https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?!["'])/g,
    (_, videoId) => {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
    }
  );

  // Also wrap existing iframes in our wrapper for consistent styling
  processedContent = processedContent.replace(
    /<iframe([^>]*youtube[^>]*)><\/iframe>/g,
    '<div class="youtube-embed-wrapper"><iframe$1></iframe></div>'
  );

  return processedContent;
}

// Helper function to get priority label
const getPriorityLabel = (priority: Priority): string => {
  if (priority >= 1000) return "Very High";
  if (priority >= 100) return "High";
  if (priority >= 10) return "Medium";
  if (priority >= 1) return "Low";
  return "Lowest";
};

// Helper function to get priority color
const getPriorityColor = (priority: Priority): string => {
  if (priority >= 1000) return "bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300";
  if (priority >= 100) return "bg-orange-100 dark:bg-orange-500/15 text-orange-800 dark:text-orange-300";
  if (priority >= 10) return "bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-300";
  if (priority >= 1) return "bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-300";
  return "bg-muted text-foreground";
};

interface FAQItemProps {
  faq: FAQ;
  index: number;
  onEdit: (faq: FAQ) => void;
  onDelete: (id: string) => void;
}

function FAQItem({ faq, index, onEdit, onDelete }: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl shadow-sm hover:shadow-md hover:border-border/50 transition-all duration-300"
    >
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full text-left flex items-center justify-between group"
            >
              <h3 className="text-lg font-semibold text-foreground group-hover:text-[#d7785b] transition-colors duration-200">
                {faq.question}
              </h3>
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="ml-4 flex-shrink-0"
              >
                <ChevronDown className="h-5 w-5 text-muted-foreground dark:text-muted-foreground" />
              </motion.div>
            </button>

            {/* Meta information */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {/* Priority Badge */}
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                  faq.priority || 0
                )}`}
              >
                Priority: {faq.priority || 0} (
                {getPriorityLabel(faq.priority || 0)})
              </span>

              {/* Target Audience Chips */}
              {faq.targetAudience && faq.targetAudience.length > 0 && (
                <div className="flex flex-wrap gap-2 ">
                  {faq.targetAudience.map((audience, index) => (
                    <span
                      key={index}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200 hover:scale-105 ${
                        audience === "BRAND"
                          ? "bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300 border border-orange-200 dark:border-orange-500/30"
                          : audience === "AFFILIATE"
                          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700"
                          : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-700"
                      }`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full mr-2 ${
                          audience === "BRAND"
                            ? "bg-[#d7785b]"
                            : audience === "AFFILIATE"
                            ? "bg-emerald-500"
                            : "bg-amber-500"
                        }`}
                      ></span>
                      {audience.charAt(0) + audience.slice(1).toLowerCase()}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions Dropdown */}
          <div className="relative ml-4">
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-2 rounded-lg hover:bg-accent transition-colors"
            >
              <MoreVertical className="h-4 w-4 text-muted-foreground dark:text-muted-foreground" />
            </button>

            {showActions && (
              <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg z-10">
                <button
                  onClick={() => {
                    onEdit(faq);
                    setShowActions(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-muted-foreground hover:bg-accent flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Edit FAQ
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(true);
                    setShowActions(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete FAQ
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FAQ Answer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: "auto",
              opacity: 1,
              transition: {
                height: { duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] },
                opacity: { duration: 0.25, delay: 0.1 },
              },
            }}
            exit={{
              height: 0,
              opacity: 0,
              transition: {
                height: { duration: 0.3, ease: "easeInOut" },
                opacity: { duration: 0.25 },
              },
            }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-4 pt-2">
              <motion.div
                initial={{ y: -8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -8, opacity: 0 }}
                transition={{
                  duration: 0.3,
                  ease: "easeOut",
                }}
                className="text-sm text-muted-foreground dark:text-muted-foreground leading-relaxed"
              >
                <ReactQuill
                  value={processYouTubeContent(faq.answer)}
                  readOnly={true}
                  theme="snow"
                  modules={{ toolbar: false }}
                  className="faq-quill-editor"
                />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside to close actions */}
      {showActions && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowActions(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          onDelete(faq.id);
          setShowDeleteModal(false);
        }}
        title="Delete FAQ"
        message="Are you sure you want to delete this FAQ?"
        itemName={faq.question}
      />
    </motion.div>
  );
}

export default function AdminFaqManager() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch FAQs on component mount
  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminFaqService.getFAQs();

      if (response) {
        setFaqs(response);
      } else {
        setError("Failed to fetch FAQs");
        toast.error("Failed to fetch FAQs");
      }
    } catch (err) {
      console.error("Failed to fetch FAQs:", err);
      setError("Failed to fetch FAQs");
      toast.error("Failed to fetch FAQs");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFaq = async (faqData: {
    question: string;
    answer: string;
    audienceTargets: ("BRAND" | "AFFILIATE" | "AGENCY")[];
    priority: number;
  }) => {
    try {
      setIsLoading(true);

      const createRequest: Partial<FAQ> = {
        question: faqData.question,
        answer: faqData.answer,
        priority: faqData.priority,
        targetAudience: faqData.audienceTargets, // Convert array to single value
      };

      const response = await adminFaqService.createFAQ(createRequest);

      if (response) {
        toast.success("FAQ created successfully");
        setShowCreateForm(false);
        // Refresh the FAQ list
        await fetchFAQs();
      }
    } catch (error: any) {
      console.error("Failed to create FAQ:", error);
      toast.error(error.message || "Failed to create FAQ");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateFaq = async (faqData: {
    question: string;
    answer: string;
    audienceTargets: ("BRAND" | "AFFILIATE" | "AGENCY")[];
    priority: number;
  }) => {
    if (!editingFaq) return;

    try {
      setIsLoading(true);

      const updateRequest: Partial<FAQ> = {
        question: faqData.question,
        answer: faqData.answer,
        priority: faqData.priority,
        targetAudience: faqData.audienceTargets, // Convert array to single value
      };

      const response = await adminFaqService.updateFAQ(
        editingFaq.id,
        updateRequest
      );

      if (response) {
        toast.success("FAQ updated successfully");
        setEditingFaq(null);
        setShowCreateForm(false);
        // Refresh the FAQ list
        await fetchFAQs();
      }
    } catch (error: any) {
      console.error("Failed to update FAQ:", error);
      toast.error(error.message || "Failed to update FAQ");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditFaq = (faq: FAQ) => {
    // If form is already open, close it first to force re-render
    if (showCreateForm) {
      setShowCreateForm(false);
      setEditingFaq(null);
      // Use setTimeout to ensure state update completes before setting new edit data
      setTimeout(() => {
        setEditingFaq(faq);
        setShowCreateForm(true);
      }, 0);
    } else {
      setEditingFaq(faq);
      setShowCreateForm(true);
    }
  };

  const handleDeleteFaq = async (id: string) => {
    try {
      setIsLoading(true);

      const response = await adminFaqService.deleteFAQ(id);

      if (response.success) {
        toast.success(response.message || "FAQ deleted successfully");
        // Refresh the FAQ list
        await fetchFAQs();
      }
    } catch (error: any) {
      console.error("Failed to delete FAQ:", error);
      toast.error(error.message || "Failed to delete FAQ");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingFaq(null);
    setShowCreateForm(false);
  };

  const getCurrentFaqData = () => {
    if (editingFaq) {
      return {
        question: editingFaq.question,
        answer: editingFaq.answer,
        audienceTargets: editingFaq.targetAudience || [],
        priority: editingFaq.priority || 10,
      };
    }
    return {
      question: "",
      answer: "",
      audienceTargets: [],
      priority: 10,
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-transparent via-muted/50 to-transparent dark:from-transparent dark:via-white/[0.02] dark:to-transparent">
      <div className="container px-4 mx-auto py-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto text-center mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="text-left">
              <h1 className="text-3xl font-semibold mb-3 bg-gradient-to-r from-foreground via-foreground/80 to-foreground bg-clip-text text-transparent">
                FAQ Management
              </h1>
              <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                Create and manage frequently asked questions for your platform
              </p>
            </div>

            <Button
              onClick={() => {
                if (showCreateForm) {
                  // If closing the form, reset everything
                  setShowCreateForm(false);
                  setEditingFaq(null);
                } else {
                  // If opening the form, make sure we're in create mode
                  setEditingFaq(null);
                  setShowCreateForm(true);
                }
              }}
              className="flex items-center gap-2 bg-[#d7785b] hover:bg-[#c86c51]"
            >
              <Plus className="h-4 w-4" />
              {showCreateForm ? "Cancel" : "Create FAQ"}
            </Button>
          </div>
        </motion.div>

        {/* FAQ Form */}
        <div className="max-w-4xl mx-auto mb-12">
          <AnimatePresence>
            {showCreateForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                {editingFaq && (
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-blue-800 dark:text-blue-300 font-medium">
                        Editing FAQ: "{editingFaq.question}"
                      </span>
                      <button
                        onClick={handleCancelEdit}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-sm underline"
                      >
                        Cancel Edit
                      </button>
                    </div>
                  </div>
                )}

                <AdminFaqForm
                  key={editingFaq ? `edit-${editingFaq.id}` : "create"}
                  onSubmit={editingFaq ? handleUpdateFaq : handleCreateFaq}
                  initialData={getCurrentFaqData()}
                  isLoading={isLoading}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* FAQ List Section */}
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-left mb-8"
          >
            <h2 className="text-2xl font-semibold mb-3 bg-gradient-to-r from-foreground via-foreground/80 to-foreground bg-clip-text text-transparent">
              All FAQs ({faqs.length})
            </h2>
            <p className="text-sm text-muted-foreground dark:text-muted-foreground">
              Sorted by priority (highest first) • Click on any question to
              expand
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d7785b]"></div>
            </div>
          ) : error ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16 bg-card/50 backdrop-blur-sm rounded-xl border border-border/50"
            >
              <div className="text-red-500 mb-4">
                <svg
                  className="w-16 h-16 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <p className="text-lg font-medium">{error}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Please try refreshing the page
                </p>
              </div>
              <Button
                onClick={fetchFAQs}
                className="bg-[#d7785b] hover:bg-[#c86c51]"
              >
                Retry
              </Button>
            </motion.div>
          ) : faqs.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center py-16 bg-card/50 backdrop-blur-sm rounded-xl border border-border/50"
            >
              <div className="inline-flex items-center justify-center p-3 rounded-full bg-muted mb-4">
                <Mail className="h-6 w-6 text-muted-foreground dark:text-muted-foreground" />
              </div>
              <p className="text-muted-foreground dark:text-muted-foreground mb-2">
                No FAQs created yet
              </p>
              <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                Click "Create FAQ" to get started with your first question
              </p>
            </motion.div>
          ) : (
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={faq.id}
                  faq={faq}
                  index={index}
                  onEdit={handleEditFaq}
                  onDelete={handleDeleteFaq}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Enhanced YouTube Embed Styles */}
      <style>{`
        .youtube-embed-wrapper {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%;
          height: 0;
          margin: 12px 0 !important;
          overflow: hidden;
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        .youtube-embed-wrapper iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          border-radius: 8px;
        }
        
        @media (max-width: 640px) {
          .youtube-embed-wrapper {
            padding-bottom: 65%;
            margin: 8px 0 !important;
          }
        }

        .faq-quill-editor .ql-editor {
          padding: 0 !important;
          font-size: 14px !important;
          line-height: 1.6 !important;
          color: inherit !important;
        }
        
        .faq-quill-editor .ql-editor p {
          margin: 1em 0 !important;
          color: inherit !important;
        }
        
        .faq-quill-editor .ql-editor strong {
          font-weight: 600 !important;
          color: inherit !important;
        }
        
        .faq-quill-editor .ql-editor em {
          font-style: italic !important;
          color: inherit !important;
        }
        
        .faq-quill-editor .ql-snow {
          border: none !important;
        }
        
        .dark .faq-quill-editor .ql-editor p,
        .dark .faq-quill-editor .ql-editor li,
        .dark .faq-quill-editor .ql-editor strong,
        .dark .faq-quill-editor .ql-editor em {
          color: rgb(156 163 175) !important;
        }
      `}</style>
    </div>
  );
}
